import { DesignTokens, Spin, Title } from "melco-ui";
import { useTranslation } from "react-i18next";
import { isEmpty } from "lodash-es";

import { CenteredMessage, StepLabel, ScrollableDiv } from "./digitizingOrdersLogsStyled";
import { DisplaySummitDetailsOrderStatus } from "./DisplaySummitDetailsMachineStatus";

const { spacings, colors } = DesignTokens;

type OrdersPanelProps = {
  selectedUser: any | null;

  /** Only sub-users have a parent account whose orders could be pulled
   * in, so the "show all orders" toggle only makes sense for them. */
  isSubUser: boolean;
  showAllOrders: boolean;
  onShowAllOrdersChange: (checked: boolean) => void;
  orderFilterText: string;
  onOrderFilterTextChange: (value: string) => void;
  ordersLoading: boolean;
  visibleOrders: any[];
  selectedOrder: any;
  onOrderSelect: (order: any, deviceId: string) => void;
};

// WHAT: the middle "Step 2 — select order" column.
// WHY a dedicated component: this used to be ~90 lines inlined in
// SummitWorkspace's JSX, mixed in with the other two panels' markup.
// Pulling it out gives it a single job (render order-selection UI for
// whatever data it's handed) and makes it possible to unit-test the
// panel's four states (no user, no add-on, loading, empty, populated)
// without also standing up the orders/logs data-fetching that lives
// in the parent.
export const OrdersPanel: React.FC<OrdersPanelProps> = ({
  selectedUser,

  orderFilterText,
  onOrderFilterTextChange,
  ordersLoading,
  visibleOrders,
  selectedOrder,
  onOrderSelect,
}) => {
  const { t } = useTranslation();

  if (!selectedUser) {
    return (
      <>
        <PanelLabel />
        <CenteredMessage>
          {t("digitizingorderslogs.select_user_prompt", {
            defaultValue: "Select a user to see their orders",
          })}
        </CenteredMessage>
      </>
    );
  }

  // if (isEmpty(selectedAddOn)) {
  //   // Validation: a user is selected but no add-on has been chosen yet,
  //   // so there's nothing to fetch orders for. Kept as its own message
  //   // (rather than folded into the "select a user" prompt above) so the
  //   // rep knows exactly what's missing instead of wondering why nothing
  //   // loaded after they already picked a user. The same guidance is now
  //   // also shown right under the add-on field itself (SelectSummitAddOn)
  //   // so it's visible at the point of the decision, not just here.
  //   return (
  //     <>
  //       <PanelLabel />
  //       <CenteredMessage>
  //         {t("digitizingorderslogs.select_addon_prompt", {
  //           defaultValue: "Select an add-on above to load this user's orders.",
  //         })}
  //       </CenteredMessage>
  //     </>
  //   );
  // }

  return (
    <>
      <PanelLabel />
      <div style={{ padding: `0px ${spacings.middle}px` }}>
        <Title level={5}>{t("digitizingorderslogs.orders")}</Title>
      </div>

      {/* {!isSubUser && (
        <ShowAllOrdersRow>
          <Checkbox
            checked={showAllOrders}
            onChange={(e: any) => onShowAllOrdersChange(!!e?.target?.checked)}
          >
            {t("digitizingorderslogs.show_all_orders", { defaultValue: "Show all orders" })}
          </Checkbox>
        </ShowAllOrdersRow>
      )} */}

      <div
        style={{
          padding: `${spacings.small}px ${spacings.middle}px`,
          borderBottom: `1px solid ${colors.gray.light}`,
        }}
      >
        <input
          type="text"
          placeholder={t("digitizingorderslogs.search_orders", { defaultValue: "Filter by name or Order ID…" })}
          value={orderFilterText}
          onChange={(e) => onOrderFilterTextChange(e.target.value)}
          style={{
            width: "100%",
            padding: "6px 9px",
            fontSize: 13,
            border: `1px solid ${colors.gray.light}`,
            borderRadius: 4,
          }}
        />
        <div style={{ fontSize: 11, color: colors.gray.middle, textAlign: "right", marginTop: 4 }}>
          {visibleOrders.length} {t("digitizingorderslogs.orders")}
        </div>
      </div>

      {ordersLoading ? (
        <CenteredMessage>
          <Spin tip={t("digitizingorderslogs.loading_orders")} />
        </CenteredMessage>
      ) : isEmpty(visibleOrders) ? (
        <CenteredMessage>{t("digitizingorderslogs.no_orders_found")}</CenteredMessage>
      ) : (
        <ScrollableDiv>
          <DisplaySummitDetailsOrderStatus
            filteredOrders={visibleOrders}
            onClick={onOrderSelect}
            selectedOrder={selectedOrder}
          />
        </ScrollableDiv>
      )}
    </>
  );
};

// Small helper so the "Step 2 — Select Order" eyebrow label doesn't
// need its padding/wrapper repeated at all four return points above.
//
// FIXME: the translation key below is `step_one_label` but the English
// fallback text says "Step 2" — this mismatch predates this refactor
// (same key was already used for the Step 2 label before). Left as-is
// rather than silently changed, since I don't know whether translation
// files elsewhere already key off `step_one_label` for this string.
// Worth confirming with whoever owns the translation files.
const PanelLabel: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div style={{ padding: `${spacings.middle}px ${spacings.middle}px 0px ${spacings.middle}px` }}>
      <StepLabel>
        {t("digitizingorderslogs.form.panel.step_one_label", { defaultValue: "Step 2 — Select Order" })}
      </StepLabel>
    </div>
  );
};
